/**
 * Wallet Earnings Context
 *
 * Global context for managing earnings records with real-time Firestore sync.
 * Provides instant writes (no debouncing) and real-time updates via onSnapshot.
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import { DocumentSnapshot, Unsubscribe } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useMaybeOLVRate } from './OLVRateContext';
import type { Earning, EarningStatus } from '../types/earnings';
import { generateWalletItemId } from '../types/walletFirestore';
import {
  createEarning,
  onEarningsSnapshot,
  listEarnings,
  CreateEarningInput,
} from '../repositories/walletEarningsRepository';

// ============================================================================
// Types
// ============================================================================

interface WalletEarningsState {
  earnings: Earning[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  hasMore: boolean;
  version: number;
  latestEarning: Earning | null;
}

interface WalletEarningsContextValue {
  state: WalletEarningsState;
  /** Add a new earning record (instant write to Firestore) */
  addEarning: (input: AddEarningInput) => Promise<Earning | null>;
  /** Load more earnings (pagination) */
  loadMore: () => Promise<void>;
  /** Refresh earnings from Firestore */
  refresh: () => void;
  /** Get an earning by ID */
  getEarningById: (id: string) => Earning | undefined;
}

export interface AddEarningInput {
  /** Summary description */
  summary: string;
  /** Source page/feature */
  source: string;
  /** Amount in USD */
  amountUsd: number;
  /** Tags for categorization */
  tags?: string[];
  /** AI model ID (optional) */
  modelId?: string;
  /** Provider name (optional) */
  provider?: string;
  /** Initial status (defaults to 'pending') */
  status?: EarningStatus;
}

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 25;

// ============================================================================
// Reducer
// ============================================================================

type WalletEarningsAction =
  | { type: 'SET_EARNINGS'; payload: { items: Earning[]; hasMore: boolean } }
  | { type: 'APPEND_EARNINGS'; payload: { items: Earning[]; hasMore: boolean } }
  | { type: 'ADD_EARNING'; payload: Earning }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR' };

const initialState: WalletEarningsState = {
  earnings: [],
  isLoading: true,
  error: null,
  totalCount: 0,
  hasMore: false,
  version: 0,
  latestEarning: null,
};

function walletEarningsReducer(state: WalletEarningsState, action: WalletEarningsAction): WalletEarningsState {
  switch (action.type) {
    case 'SET_EARNINGS':
      return {
        ...state,
        earnings: action.payload.items,
        totalCount: action.payload.items.length,
        hasMore: action.payload.hasMore,
        isLoading: false,
        error: null,
        version: state.version + 1,
      };
    case 'APPEND_EARNINGS':
      const combined = [...state.earnings, ...action.payload.items];
      return {
        ...state,
        earnings: combined,
        totalCount: combined.length,
        hasMore: action.payload.hasMore,
        isLoading: false,
        version: state.version + 1,
      };
    case 'ADD_EARNING':
      const newEarnings = [action.payload, ...state.earnings];
      return {
        ...state,
        earnings: newEarnings,
        totalCount: state.totalCount + 1,
        version: state.version + 1,
        latestEarning: action.payload,
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR':
      return initialState;
    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

const WalletEarningsContext = createContext<WalletEarningsContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export function WalletEarningsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const olvRate = useMaybeOLVRate();
  const [state, dispatch] = useReducer(walletEarningsReducer, initialState);
  const cursorRef = useRef<DocumentSnapshot | null>(null);
  const unsubRef = useRef<Unsubscribe | null>(null);

  // Set up real-time listener when user changes
  useEffect(() => {
    if (!user?.id) {
      dispatch({ type: 'CLEAR' });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    // Clean up previous listener
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    // Start real-time sync
    unsubRef.current = onEarningsSnapshot(
      user.id,
      { limit: PAGE_SIZE },
      (items, nextCursor) => {
        cursorRef.current = nextCursor;
        dispatch({
          type: 'SET_EARNINGS',
          payload: { items, hasMore: items.length >= PAGE_SIZE },
        });
      },
      (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    );

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [user?.id]);

  // Add a new earning (instant write)
  const addEarning = useCallback(
    async (input: AddEarningInput): Promise<Earning | null> => {
      if (!user?.id) return null;

      const rate = olvRate?.getEffectiveRate() || 0.001;
      const memberId = user.profileId || user.id;

      const createInput: CreateEarningInput = {
        summary: input.summary,
        source: input.source,
        memberId,
        amountUsd: input.amountUsd,
        amountOlv: input.amountUsd / rate,
        olvRateAtTime: rate,
        tags: input.tags,
        modelId: input.modelId,
        provider: input.provider,
        status: input.status || 'pending',
      };

      try {
        const earning = await createEarning(user.id, createInput);
        // The real-time listener will update the state automatically
        // But we also add it optimistically for instant UI feedback
        dispatch({ type: 'ADD_EARNING', payload: earning });
        return earning;
      } catch (error) {
        console.error('[WalletEarningsContext] Failed to create earning:', error);
        dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
        return null;
      }
    },
    [user?.id, user?.profileId, olvRate]
  );

  // Load more earnings (pagination)
  const loadMore = useCallback(async () => {
    if (!user?.id || !cursorRef.current || state.isLoading) return;

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const result = await listEarnings(user.id, {
        limit: PAGE_SIZE,
        startAfter: cursorRef.current,
      });

      cursorRef.current = result.nextCursor;
      dispatch({
        type: 'APPEND_EARNINGS',
        payload: { items: result.items, hasMore: result.items.length >= PAGE_SIZE },
      });
    } catch (error) {
      console.error('[WalletEarningsContext] Failed to load more earnings:', error);
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
    }
  }, [user?.id, state.isLoading]);

  // Refresh earnings
  const refresh = useCallback(() => {
    if (!user?.id) return;

    // Clean up and re-subscribe to trigger a fresh load
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    unsubRef.current = onEarningsSnapshot(
      user.id,
      { limit: PAGE_SIZE },
      (items, nextCursor) => {
        cursorRef.current = nextCursor;
        dispatch({
          type: 'SET_EARNINGS',
          payload: { items, hasMore: items.length >= PAGE_SIZE },
        });
      },
      (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    );
  }, [user?.id]);

  // Get earning by ID
  const getEarningById = useCallback(
    (id: string): Earning | undefined => {
      return state.earnings.find((e) => e.id === id);
    },
    [state.earnings]
  );

  const value: WalletEarningsContextValue = {
    state,
    addEarning,
    loadMore,
    refresh,
    getEarningById,
  };

  return (
    <WalletEarningsContext.Provider value={value}>
      {children}
    </WalletEarningsContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useWalletEarnings(): WalletEarningsContextValue {
  const context = useContext(WalletEarningsContext);
  if (!context) {
    throw new Error('useWalletEarnings must be used within a WalletEarningsProvider');
  }
  return context;
}

/**
 * Optional hook that returns null if not within provider
 */
export function useMaybeWalletEarnings(): WalletEarningsContextValue | null {
  return useContext(WalletEarningsContext);
}
